import * as React from 'react'

import { TLanguage } from '~config/locales'
import { formatNumber } from '~logic/helpers/formatNumber'
import { TProductDetailState } from '~logic/xstate/machines/menu/productDetailMachineTypes'
import { useProductDetailMachine } from '~shared/segments/ProductDetail'

// TODO optimise with useSelector

type TProps = NoChildren & {
  selectedLanguage: TLanguage
}

/**
 * Encapsulates some logic of the product detail modal OR mobile drawer.
 * Returns useful values that can be used by the ProductDetail components.
 */
export const useProductDetailStateValues = ({ selectedLanguage }: TProps) => {
  const [state] = useProductDetailMachine()

  const isLoading = !state.matches('loaded.editing')
  const canSubmit = !isLoading && state.context.addonCategoriesFulfilled

  // calculates and formats the price of the product with selected addons
  // as a string of the price with currency label
  const productPriceSum = React.useMemo(() => {
    if (isLoading) {
      return null
    }

    const { product, addonCategories, amount } = state.context
    const { isCombinationMeal, price } = product

    const { value: combinationMealPrice } = product.combinationMealPrice
    const { currency, value: normalPrice } = price
    const { code: currencyLabel, decimalPlaces } = currency

    const productPrice = isCombinationMeal ? combinationMealPrice : normalPrice
    const addonPrice = getAddonPriceSum({ addonCategories, isCombinationMeal })

    const individualItemPrice = productPrice + addonPrice
    const productPriceSum = amount * individualItemPrice

    const formattedPriceSum = formatNumber({
      value: productPriceSum,
      maxDecimalPlaces: decimalPlaces,
      locale: selectedLanguage.code,
    })

    return `${formattedPriceSum} ${currencyLabel}`
  }, [isLoading, state.context, selectedLanguage.code])

  // Get the addon categories with the correct prices.
  // We need to show `sideDishCombinationMealPrice` instead of `price` for combination-meal addons.
  // But only when the selected addons are not over the minimum count.
  // If the selected addons gets over the minimum count, we need to start showing normal prices,
  // because the price of addon is not included in the combination meal price, but is added on top of it.
  const addonCategories = React.useMemo(() => {
    if (!state.context.addonCategories) return null

    const addonCategoriesWithCorrectPrices = state.context.addonCategories.map((category) => {
      const selectedAddonCount = getSelectedAddonCount(category)
      const isOverMinCount = selectedAddonCount >= category.minTotalCount
      const isFull = category.maxTotalCount > 0 && selectedAddonCount >= category.maxTotalCount
      const hasSameMaxAndMinCount =
        category.maxTotalCount !== 0 &&
        category.minTotalCount !== 0 &&
        category.maxTotalCount <= category.minTotalCount

      const addons = category.addons.map(({ sideDishCombinationMealPrices, ...addon }) => {
        const isCombinationMeal = Boolean(state.context.product?.isCombinationMeal)
        const shouldShowCombinationMealPrices =
          (isCombinationMeal && !isOverMinCount) || // show combination meal prices until selected addons are not over the minimum count
          (isCombinationMeal && isFull && hasSameMaxAndMinCount) // keep showing combination meal prices if max count is reached

        const combinationMealPrice = sideDishCombinationMealPrices?.find(
          (price) => price.categoryId === category.id,
        )
        const combinationMealPriceWithFallback = combinationMealPrice?.price ?? addon.price
        const price = shouldShowCombinationMealPrices
          ? combinationMealPriceWithFallback
          : addon.price

        return { ...addon, price }
      })

      return { ...category, addons }
    })

    return addonCategoriesWithCorrectPrices
  }, [state.context.addonCategories, state.context.product])

  return {
    isLoading,
    canSubmit,
    productPriceSum,
    productNote: state.context.note,
    productAmount: state.context.amount,
    productDetail: state.context.product,
    addonCategories,
  }
}

type TGetAddonPriceSumOptions = {
  isCombinationMeal: boolean
  addonCategories: TProductDetailState['addonCategories']
}

// add a "price * amount" for each selected addon in each addon category
const getAddonPriceSum = ({ addonCategories, isCombinationMeal }: TGetAddonPriceSumOptions) => {
  return addonCategories.reduce((sum, category) => {
    const categoryTotal = getCategoryTotal(category)
    const includedAddonPrice = isCombinationMeal ? getIncludedAddonPrice(category) : 0

    return sum + categoryTotal - includedAddonPrice
  }, 0)
}

// * HELPERS

type TAddonCategory = TProductDetailState['addonCategories'][number]

// count the total amount of selected addons in the category
const getSelectedAddonCount = (category: TAddonCategory) => {
  return category.addons.reduce((sum, addon) => sum + addon.amount, 0)
}

// get the sum of the addons included in the combination meal price
// this sum will be subtracted from the total addon price if the product is a combination meal
const getIncludedAddonPrice = (category: TAddonCategory) => {
  const selectedAddonCount = getSelectedAddonCount(category)
  const prices = category.addons.map((addon) => addon.price.value)
  const minPrice = Math.min(...prices)

  const includedAddonCount = Math.min(selectedAddonCount, category.minTotalCount)
  const includedAddonPrice = includedAddonCount * minPrice

  return includedAddonPrice
}

// get the total price of the selected addons in the category
const getCategoryTotal = (category: TAddonCategory) => {
  const categoryTotal = category.addons.reduce((categorySum, addon) => {
    const addonPrice = addon.amount * addon.price.value

    return categorySum + addonPrice
  }, 0)

  return categoryTotal
}
